import { mulberry32, shuffleInPlace } from '../utils/prng.js';
import {
  COPIES_PER_KIND,
  HAND_SIZE,
  HONORS,
  RANKS,
  SEAT_COUNT,
  SUITS,
  type Tile,
} from './types.js';

export interface DealResult {
  readonly hands: readonly (readonly Tile[])[];
  readonly wall: readonly Tile[];
}

/** Conventional dead-wall reserve size; kong replacement draws come from here, not the live wall. */
export const DEAD_WALL_SIZE = 16;

export interface DeadWallSplit {
  readonly liveWall: readonly Tile[];
  readonly deadWall: readonly Tile[];
}

/**
 * All 136 tiles (34 kinds × 4 copies), shuffled deterministically from
 * `seed`. Tile ids are assigned in canonical pre-shuffle order, so a given
 * (kind, copy) pair always carries the same id regardless of seed.
 */
export function buildWall(seed: number): Tile[] {
  const tiles: Tile[] = [];
  let id = 0;
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      for (let copy = 0; copy < COPIES_PER_KIND; copy++) {
        tiles.push({ kind: 'suit', suit, rank, id: id++ });
      }
    }
  }
  for (const honor of HONORS) {
    for (let copy = 0; copy < COPIES_PER_KIND; copy++) {
      tiles.push({ kind: 'honor', honor, id: id++ });
    }
  }
  return shuffleInPlace(mulberry32(seed), tiles);
}

/**
 * Draws 13 tiles for each of the 4 seats off the front of the wall. Pure —
 * the input wall is untouched; the returned wall is what remains. The
 * dealer's 14th tile is NOT dealt here: the turn engine draws it as the
 * first draw of the hand.
 */
export function dealHands(wall: readonly Tile[]): DealResult {
  const hands: Tile[][] = [];
  for (let seat = 0; seat < SEAT_COUNT; seat++) {
    const start = seat * HAND_SIZE;
    hands.push(wall.slice(start, start + HAND_SIZE));
  }
  return { hands, wall: wall.slice(SEAT_COUNT * HAND_SIZE) };
}

/**
 * Carves the last `DEAD_WALL_SIZE` tiles off a wall (e.g. `dealHands`'s
 * remainder) as a reserved dead wall — kong replacement draws come from
 * here, so declaring a kong never eats into the live wall's draw count.
 * Pure; the input wall is untouched. Callers with a wall shorter than
 * `DEAD_WALL_SIZE` get the whole thing back as the dead wall and an empty
 * live wall, rather than throwing.
 */
export function splitDeadWall(wall: readonly Tile[]): DeadWallSplit {
  const liveCount = Math.max(0, wall.length - DEAD_WALL_SIZE);
  return { liveWall: wall.slice(0, liveCount), deadWall: wall.slice(liveCount) };
}
